import React, { useState } from "react";
import { Link } from "react-router-dom";
import Panel from "@/components/Panel";
import LoadingSpinner from "@/components/LoadingSpinner";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useMcpServers, { McpServerConfig } from "@/hooks/useMcpServers";
import McpServerModal from "./McpServerModal";
import McpServerRow from "./McpServerRow";
import useApi from "@/hooks/useApi";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import IntegrationActivityLog from "../IntegrationActivityLog";

// ─── MCP Library ─────────────────────────────────────────────────────────────

interface McpLibraryEntry {
  id: string;
  name: string;
  description: string;
  category: string;
  docsUrl: string;
  prefillUrl?: string;
  apiKeyHint?: string;
}

const MCP_LIBRARY: McpLibraryEntry[] = [
  {
    id: "exa",
    name: "Exa Search",
    category: "Web Search",
    description: "AI-powered semantic web search. Find recent news, research papers, and any web content using natural-language queries.",
    docsUrl: "https://docs.exa.ai/reference/mcp-server",
    prefillUrl: "https://mcp.exa.ai",
    apiKeyHint: "Get your API key at dashboard.exa.ai"
  },
  {
    id: "tavily",
    name: "Tavily Search",
    category: "Web Search",
    description: "Real-time web search optimised for AI agents. Returns clean, summarised results perfect for answering customer questions.",
    docsUrl: "https://docs.tavily.com/docs/mcp",
    prefillUrl: "https://mcp.tavily.com",
    apiKeyHint: "Get your API key at app.tavily.com"
  },
  {
    id: "stripe",
    name: "Stripe",
    category: "Payments",
    description: "Look up customers, charges, subscriptions, and invoices during live chats so agents never have to switch tabs.",
    docsUrl: "https://stripe.com/docs/building-with-llms/mcp",
    apiKeyHint: "Use a restricted API key from your Stripe Dashboard"
  },
  {
    id: "github",
    name: "GitHub",
    category: "Developer Tools",
    description: "Search repositories, list issues, and look up pull requests — useful for SaaS support teams answering technical questions.",
    docsUrl: "https://github.com/modelcontextprotocol/servers/tree/main/src/github",
    apiKeyHint: "Create a Personal Access Token (classic) with read:org and repo scopes"
  },
  {
    id: "notion",
    name: "Notion",
    category: "Knowledge",
    description: "Query your Notion workspace — great for surfacing internal SOPs, FAQs, and knowledge during support conversations.",
    docsUrl: "https://github.com/modelcontextprotocol/servers/tree/main/src/notion",
    apiKeyHint: "Create an internal integration at notion.so/my-integrations"
  },
  {
    id: "linear",
    name: "Linear",
    category: "Project Management",
    description: "Look up issues and project status in Linear. Lets support agents check bug status or feature requests in real time.",
    docsUrl: "https://github.com/modelcontextprotocol/servers/tree/main/src/linear",
    apiKeyHint: "Create a personal API key in Linear Settings → API"
  }
];

const CATEGORY_COLORS: Record<string, string> = {
  "Web Search": "bg-blue-50 text-blue-700",
  "Payments": "bg-green-50 text-green-700",
  "CRM": "bg-purple-50 text-purple-700",
  "E-commerce": "bg-orange-50 text-orange-700",
  "Developer Tools": "bg-slate-100 text-slate-700",
  "Knowledge": "bg-yellow-50 text-yellow-700",
  "Project Management": "bg-pink-50 text-pink-700"
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function McpServers() {
  const { items, loading, setItems } = useMcpServers();
  const { enableMcp, maxMcpServers, maxMcpCallsPerConversation, maxMcpCallsPerMonth } = useAdminFeatures();
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<McpServerConfig | null>(null);
  const [prefill, setPrefill] = useState<{ name?: string; description?: string; url?: string } | undefined>();
  const [showLibrary, setShowLibrary] = useState(false);
  const api = useApi();

  const atLimit = items.length >= maxMcpServers;

  function openAdd() {
    setEditTarget(null);
    setPrefill(undefined);
    setShowModal(true);
  }

  function openAddFromLibrary(entry: McpLibraryEntry) {
    setEditTarget(null);
    setPrefill({
      name: entry.name,
      description: entry.description,
      url: entry.prefillUrl
    });
    setShowModal(true);
  }

  function openEdit(config: McpServerConfig) {
    setEditTarget(config);
    setPrefill(undefined);
    setShowModal(true);
  }

  function handleSaved(saved: McpServerConfig) {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
    setShowModal(false);
  }

  async function handleDelete(id: number) {
    try {
      await api.admin.delete(`McpServers/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch {
      // errors surfaced in McpServerRow
    }
  }

  async function handleDiscover(config: McpServerConfig) {
    try {
      const resp = await api.admin.post(`McpServers/${config.id}/discover`, {});
      if (resp.ok) {
        const updated = await resp.json() as McpServerConfig;
        setItems((prev) =>
          prev.map((x) => (x.id === updated.id ? updated : x))
        );
      }
    } catch {
      // errors surfaced in McpServerRow
    }
  }

  return (
    <Panel title="MCP Servers">
      <div className="p-4">
        <p className="text-sm text-slate-600 mb-3">
          Connect any external tool or API to your AI bots using the open MCP (Model Context Protocol) standard.
          MCP is designed for systems that don't have a dedicated Velaro integration — custom internal APIs,
          niche SaaS tools, or anything you've built in-house.
        </p>
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <span className="font-medium">Using HubSpot, Shopify, BigCommerce, Dynamics, NetSuite, or another major platform?</span>{" "}
          Use the dedicated integration pages instead — they include purpose-built AI skills (20–35 per platform),
          OAuth setup, transcript push, and sidebar fields. MCP is not needed for those.{" "}
          <Link to="/integrations" className="underline font-medium">Browse integrations →</Link>
        </div>

        {/* Subscription gate */}
        {!enableMcp && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            MCP servers are not included in your current plan.{" "}
            <a href="mailto:sales@velaro.com" className="underline font-medium">Contact sales</a> to enable this feature.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Usage counter + Add button */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500">
                {items.length} / {maxMcpServers} server{maxMcpServers !== 1 ? "s" : ""} used
              </span>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowLibrary((s) => !s)}
                  className="text-sm text-blue-600 hover:underline font-medium"
                >
                  {showLibrary ? "Hide library" : "Browse integrations"}
                </button>
                <PrimaryBlueButton
                  label="Add MCP Server"
                  onClick={openAdd}
                  disabled={!enableMcp || atLimit}
                />
              </div>
            </div>

            {atLimit && enableMcp && (
              <p className="text-xs text-amber-700 mb-3">
                You've reached the {maxMcpServers}-server limit. Remove an existing server or{" "}
                <a href="mailto:sales@velaro.com" className="underline">upgrade your plan</a> to add more.
              </p>
            )}

            {/* Configured servers table */}
            {items.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">
                No MCP servers configured yet. Add one below or browse the integration library.
              </p>
            ) : (
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto mb-4">
                <table className="w-full bg-slate-50 text-sm">
                  <thead>
                    <tr>
                      <th className="border-b p-2 text-left">Name</th>
                      <th className="border-b p-2 text-left">URL</th>
                      <th className="border-b p-2 text-left">Tools</th>
                      <th className="border-b p-2 text-left">Status</th>
                      <th className="border-b p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((config) => (
                      <McpServerRow
                        key={config.id}
                        config={config}
                        onEdit={() => openEdit(config)}
                        onDelete={() => handleDelete(config.id)}
                        onDiscover={() => handleDiscover(config)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* MCP Library */}
            {showLibrary && (
              <div className="mt-4 border-t pt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  Popular Integrations
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {MCP_LIBRARY.map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col gap-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-medium text-slate-800">{entry.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[entry.category] ?? "bg-slate-100 text-slate-600"}`}>
                              {entry.category}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 leading-snug">{entry.description}</p>
                          {entry.apiKeyHint && (
                            <p className="text-xs text-slate-400 mt-1 italic">{entry.apiKeyHint}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-auto">
                        <a
                          href={entry.docsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          View docs
                        </a>
                        <button
                          onClick={() => openAddFromLibrary(entry)}
                          disabled={!enableMcp || atLimit}
                          className="ml-auto text-xs px-3 py-1 rounded-md border border-blue-500 text-blue-600 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Call limits panel */}
      {enableMcp && (
        <div className="border-t border-slate-100 px-4 py-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Usage Limits
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500 mb-1">MCP Servers</div>
              <div className="text-lg font-bold text-slate-800">
                {items.length} <span className="text-sm font-normal text-slate-400">/ {maxMcpServers}</span>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">Configured connections</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500 mb-1">Per-Conversation Limit</div>
              <div className="text-lg font-bold text-slate-800">
                {maxMcpCallsPerConversation}
                <span className="text-sm font-normal text-slate-400"> calls</span>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">Max tool calls per chat</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-500 mb-1">Monthly Budget</div>
              <div className="text-lg font-bold text-slate-800">
                {maxMcpCallsPerMonth.toLocaleString()}
                <span className="text-sm font-normal text-slate-400"> calls</span>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">Resets each calendar month</div>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">
            When a limit is reached the AI continues the conversation without MCP tools — chats never crash.
            Contact <a href="mailto:sales@velaro.com" className="underline text-slate-500">sales@velaro.com</a> to increase limits.
          </p>
        </div>
      )}

      {showModal && (
        <McpServerModal
          existing={editTarget}
          prefill={prefill}
          onSaved={handleSaved}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Activity Log */}
      <div className="mt-8 px-4 pb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="MCP" />
      </div>
    </Panel>
  );
}
